import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { usePermissions } from './usePermissions';

interface DataAssignment {
  id: string;
  member_id: string;
  table_name: string;
  record_id: string;
}

interface AssignmentsState {
  memberId: string | null;
  assignments: DataAssignment[];
  loading: boolean;
  byTable: Record<string, string[]>; // table_name -> record_ids[]
}

export function useDataAssignments() {
  const { user } = useAuth();
  const { isAdminOrOwner, loading: permissionsLoading } = usePermissions();
  
  const [state, setState] = useState<AssignmentsState>({
    memberId: null,
    assignments: [],
    loading: true,
    byTable: {},
  });

  const fetchAssignments = useCallback(async () => {
    if (!user) {
      setState({
        memberId: null,
        assignments: [],
        loading: false,
        byTable: {},
      });
      return;
    }

    try {
      // First get the member_id for this user
      // Use secure view to prevent access to invited_email column
      const { data: memberData, error: memberError } = await supabase
        .from('organization_members_secure')
        .select('id, role')
        .eq('user_id', user.id)
        .or('accepted_at.not.is.null,role.eq.owner')
        .single();

      if (memberError || !memberData) {
        console.error('Error fetching member for assignments:', memberError);
        setState({
          memberId: null,
          assignments: [],
          loading: false,
          byTable: {},
        });
        return;
      }

      // Owner and Admin see all data - no filtering needed
      if (memberData.role === 'owner' || memberData.role === 'admin') {
        setState({
          memberId: memberData.id,
          assignments: [],
          loading: false,
          byTable: {},
        });
        return;
      }

      // Fetch data assignments for this member
      const { data: assignmentsData, error: assignmentsError } = await supabase
        .from('data_assignments')
        .select('*')
        .eq('member_id', memberData.id);

      if (assignmentsError) {
        console.error('Error fetching data assignments:', assignmentsError);
        setState({
          memberId: memberData.id,
          assignments: [],
          loading: false,
          byTable: {},
        });
        return;
      }

      // Group assignments by table
      const byTable: Record<string, string[]> = {};
      (assignmentsData || []).forEach((assignment) => {
        if (!byTable[assignment.table_name]) {
          byTable[assignment.table_name] = [];
        }
        byTable[assignment.table_name].push(assignment.record_id);
      });

      setState({
        memberId: memberData.id,
        assignments: assignmentsData || [],
        loading: false,
        byTable,
      });

    } catch (error) {
      console.error('Error in fetchAssignments:', error);
      setState({
        memberId: null,
        assignments: [],
        loading: false,
        byTable: {},
      });
    }
  }, [user]);

  useEffect(() => {
    // Wait for permissions to load first
    if (!permissionsLoading) {
      fetchAssignments();
    }
  }, [fetchAssignments, permissionsLoading]);

  /**
   * Check if a specific record is assigned to the member
   * Returns true if:
   * - User is owner/admin (sees everything)
   * - User is member with no assignments for this table (sees everything)
   * - User is member and this specific record is assigned
   */
  const isRecordAssigned = useCallback((tableName: string, recordId: string): boolean => {
    // Owner/Admin see everything
    if (isAdminOrOwner) {
      return true;
    }

    const tableAssignments = state.byTable[tableName];
    
    // If no assignments exist for this table, member sees all records
    if (!tableAssignments || tableAssignments.length === 0) {
      return true;
    }

    // Check if this specific record is assigned
    return tableAssignments.includes(recordId);
  }, [isAdminOrOwner, state.byTable]);

  /**
   * Filter a list of records by data assignments
   * Returns only records that are assigned to the member
   */
  const filterByAssignments = useCallback(<T extends { id: string }>(
    items: T[],
    tableName: string
  ): T[] => {
    // Owner/Admin see everything
    if (isAdminOrOwner) {
      return items;
    }

    const tableAssignments = state.byTable[tableName];
    
    // If no assignments exist for this table, member sees all records
    if (!tableAssignments || tableAssignments.length === 0) {
      return items;
    }

    // Filter to only assigned records
    return items.filter(item => tableAssignments.includes(item.id));
  }, [isAdminOrOwner, state.byTable]);

  /**
   * Get array of assigned record IDs for a specific table
   * Returns null if user has access to all records (owner/admin or no assignments)
   */
  const getAssignedIds = useCallback((tableName: string): string[] | null => {
    if (isAdminOrOwner) {
      return null; // null means "all access"
    }

    const tableAssignments = state.byTable[tableName];
    
    if (!tableAssignments || tableAssignments.length === 0) {
      return null; // null means "all access" when no assignments exist
    }

    return tableAssignments;
  }, [isAdminOrOwner, state.byTable]);

  /**
   * Check if member has any data assignments (for any table)
   */
  const hasAnyAssignments = useMemo(() => {
    return Object.keys(state.byTable).some(
      table => state.byTable[table].length > 0
    );
  }, [state.byTable]);

  return {
    memberId: state.memberId,
    assignments: state.assignments,
    loading: state.loading || permissionsLoading,
    byTable: state.byTable,
    isRecordAssigned,
    filterByAssignments,
    getAssignedIds,
    hasAnyAssignments,
    refresh: fetchAssignments,
  };
}
